import { ChangeEvent, useState } from "react"
import styled from "styled-components"

import {
  useLazyCreatePublicReferralClaimQuery,
  useLazyGetPublicCitiesQuery,
  useLazyGetPublicStatesQuery,
} from "store/api/publicApi"
import { UITypes } from "types"

import { onFormValidate, publicReferralClaimSchema, validationInitialState } from "utils/onValidate"
import { InputNew } from "components/form/InputNew"
import { InputPhone } from "components/form/InputPhone"
import { SelectDynamic } from "components/form/SelectDynamic"

import { ClaimCard } from "./ClaimCard"
import { mobileBreakpoint, PrimaryButton, textGrey, useReferralAgent } from "./shared"

const FieldRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;

  /* the form components carry their own (inconsistent) bottom margins; the row owns the spacing instead */
  > div {
    flex: 1;
    min-width: 0;
    margin-bottom: 0;
  }

  @media only screen and (width <= ${mobileBreakpoint}) {
    flex-direction: column;
    gap: 16px;
  }
`

const FieldError = styled.span`
  margin-top: -12px;
  margin-bottom: 16px;
  color: #ef4444;
  font-size: 0.75rem;
  font-weight: 500;
  display: block;
`

const ClaimNote = styled.p`
  margin: 14px 0 0;
  color: ${textGrey};
  font-size: 0.75rem;
  text-align: center;
`

const SubmitError = styled.div`
  margin-bottom: 16px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.8125rem;
  line-height: 1.5;
`

const US_COUNTRY_ID = 1

const CLAIM_FIELD_MAPPING: Record<string, string> = {
  homeowner_name: "name",
  homeowner_email: "email",
  homeowner_phone: "phone",
  property_address1: "address",
  city_id: "city",
  state_id: "state",
}

interface ClaimValues {
  address: string
  city: UITypes.Option[]
  state: UITypes.Option[]
  name: string
  email: string
  phone: string
}

const claimInitialState: ClaimValues = {
  address: "",
  city: [],
  state: [],
  name: "",
  email: "",
  phone: "",
}

interface Props {
  onSuccess: () => void
}

export const ClaimForm = ({ onSuccess }: Props) => {
  const [values, setValues] = useState<ClaimValues>(claimInitialState)
  const [error, setError] = useState(validationInitialState)
  const [submitError, setSubmitError] = useState("")
  const [triggerCreateClaim, { isFetching: isSubmitting }] = useLazyCreatePublicReferralClaimQuery()
  const agent = useReferralAgent()
  const firstName = agent?.name.split(" ")[0] ?? "your agent"

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget
    setValues(prevState => ({ ...prevState, [name]: value }))
  }

  const onStateChange = (state: UITypes.Option[]) => {
    if (state[0]?.value === values.state[0]?.value) return
    // cities are scoped to the selected state, so a state change clears the city
    setValues(prevState => ({ ...prevState, state, city: [] }))
  }

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitError("")
    if (!onFormValidate(values, publicReferralClaimSchema, setError) || !agent) return

    const res = await triggerCreateClaim({
      slug: agent.slug,
      body: {
        homeowner_name: values.name.trim(),
        homeowner_email: values.email.trim() || undefined,
        homeowner_phone: values.phone.trim() || undefined,
        property_address1: values.address.trim(),
        city_id: Number(values.city[0].value),
        state_id: Number(values.state[0].value),
      },
    })

    if (!res.error) {
      onSuccess()
      return
    }

    // entered data is kept so the homeowner can fix and retry
    const status = "status" in res.error ? res.error.status : undefined
    const payload =
      "data" in res.error ? (res.error.data as { message?: string; errors?: Record<string, string[]> }) : undefined
    const [serverField, messages] = Object.entries(payload?.errors ?? {})[0] ?? []

    if (status === 422 && serverField) {
      setError({
        field: CLAIM_FIELD_MAPPING[serverField] ?? serverField,
        message: messages?.[0] ?? "Please check this field",
      })
    } else if (status === 429) {
      setSubmitError("Too many attempts right now. Please wait a moment and try again.")
    } else if (status === 404) {
      setSubmitError("This referral link is no longer active.")
    } else if (status === 403) {
      setSubmitError("A claim for this address has already been submitted. Your agent will be in touch.")
    } else {
      setSubmitError("We couldn't submit your claim. Please check your connection and try again.")
    }
  }

  // the at-least-one-contact rule reports no single field, so it renders under the contact row
  const contactError = error.message && !error.field ? <FieldError>{error.message}</FieldError> : null

  return (
    <ClaimCard onSubmit={onSubmit}>
      <h2>Claim your thermostat</h2>
      <p>We&apos;ll confirm your address and schedule a certified installer.</p>
      <InputNew
        label="Property address"
        placeholder="Street address"
        name="address"
        value={values.address}
        onChange={onInputChange}
        errorMessage={error.field === "address" ? error.message : ""}
      />
      <FieldRow>
        <SelectDynamic
          label="State"
          placeholder="Select state"
          name="state"
          valueName="id"
          labelName="name"
          values={values.state}
          useLazyQuery={useLazyGetPublicStatesQuery}
          queryParams={{ country_id: US_COUNTRY_ID, limit: 60 }}
          onChange={onStateChange}
          errorMessage={error.field === "state" ? error.message : ""}
        />
        <SelectDynamic
          label="City"
          placeholder={values.state.length ? "Select city" : "Select state first"}
          name="city"
          valueName="id"
          labelName="name"
          values={values.city}
          disabled={!values.state.length}
          useLazyQuery={useLazyGetPublicCitiesQuery}
          queryParams={{
            state_id: values.state[0]?.value != null ? String(values.state[0].value) : undefined,
            limit: 50,
          }}
          onChange={city => setValues(prevState => ({ ...prevState, city }))}
          errorMessage={error.field === "city" ? error.message : ""}
        />
      </FieldRow>
      <InputNew
        label="Your name"
        placeholder="Enter your name"
        name="name"
        value={values.name}
        onChange={onInputChange}
        errorMessage={error.field === "name" ? error.message : ""}
      />
      <FieldRow>
        <InputNew
          label="Email (Optional)"
          placeholder="you@example.com"
          name="email"
          value={values.email}
          onChange={onInputChange}
          errorMessage={error.field === "email" ? error.message : ""}
        />
        <InputPhone
          label="Phone"
          placeholder="(512) 555-0000"
          name="phone"
          value={values.phone}
          onChange={value => setValues(prevState => ({ ...prevState, phone: value }))}
          errorMessage={error.field === "phone" ? error.message : ""}
        />
      </FieldRow>
      {contactError}
      {submitError && <SubmitError role="alert">{submitError}</SubmitError>}
      <PrimaryButton type="submit" disabled={isSubmitting}>
        {isSubmitting ? "Submitting…" : "Claim my thermostat"} <span aria-hidden>→</span>
      </PrimaryButton>
      <ClaimNote>{`Only ${firstName} sees you claimed. Nothing shared publicly.`}</ClaimNote>
    </ClaimCard>
  )
}
